import type { ReactNode } from 'react';
import type { PressEvent } from 'react-aria';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { IconButton } from '@launchpad-ui/components';
import clsx from 'clsx';

import styles from './Pagination.module.css';

type PaginationRenderProps = {
  from: number;
  to: number;
  total: number | string;
};

type PaginationProps = {
  from: number;
  to: number;
  total: number | string;
  onFirstClick?: (e: PressEvent) => void;
  onPreviousClick?: (e: PressEvent) => void;
  onNextClick?: (e: PressEvent) => void;
  onLastClick?: (e: PressEvent) => void;
  className?: string;
  children?: ((values: PaginationRenderProps) => ReactNode) | undefined;
};

export function Pagination({
  from,
  to,
  total,
  className,
  children,
  onFirstClick,
  onPreviousClick,
  onNextClick,
  onLastClick,
}: PaginationProps) {
  return (
    <div className={clsx(styles.pagination, className)}>
      <IconButton
        size="small"
        icon="chevrons-left"
        aria-label={msg('ui.pagination.firstPage')}
        isDisabled={onFirstClick === undefined}
        onPress={onFirstClick}
        variant="minimal"
      />
      <IconButton
        size="small"
        icon="chevron-left"
        aria-label={msg('ui.pagination.previousPage')}
        isDisabled={onPreviousClick === undefined}
        onPress={onPreviousClick}
        variant="minimal"
      />
      <div className={styles.hud}>
        {typeof children === 'function' ? (
          children({ from, to, total })
        ) : (
          <Message
            id="ui.pagination.range"
            values={{
              from,
              to,
              total,
              range: (chunks) => <strong>{chunks}</strong>,
              count: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        )}
      </div>
      <IconButton
        size="small"
        icon="chevron-right"
        aria-label={msg('ui.pagination.nextPage')}
        isDisabled={onNextClick === undefined}
        onPress={onNextClick}
        variant="minimal"
      />
      <IconButton
        size="small"
        icon="chevrons-right"
        aria-label={msg('ui.pagination.lastPage')}
        isDisabled={onLastClick === undefined}
        onPress={onLastClick}
        variant="minimal"
      />
    </div>
  );
}

export const PaginationFallback = () => (
  <div className={styles.pagination}>
    <div className={styles.hud}>
      <Message id="ui.pagination.loadError" />
    </div>
  </div>
);
